import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Flame, Snowflake, Gauge, ArrowRight, ChevronRight, ShieldCheck, Home } from 'lucide-react';

const services = [
  {
    id: 'plumbing',
    name: 'Plumbing',
    icon: Droplets,
    color: '#3B82F6',
    description: 'Complete plumbing services for residential & light commercial',
    path: '/plumbing',
    services: [
      'Poly-B Replacement',
      'Tankless Water Heater Installation',
      'Hot Water Tank Replacement',
      'Leak Repair',
      'Drain Cleaning',
      'Bathroom & Kitchen Plumbing',
      'Renovation Plumbing',
      'New Home Plumbing'
    ]
  },
  {
    id: 'heating',
    name: 'Heating',
    icon: Flame,
    color: '#EF4444',
    description: 'Expert heating solutions including in-floor & hydronic',
    path: '/heating',
    services: [
      'Furnace Installation & Repair',
      'In-Floor Radiant Heating',
      'Boiler & Hydronic Systems',
      'Garage Heaters',
      'Thermostats',
      'Furnace Maintenance'
    ]
  },
  {
    id: 'air-conditioning',
    name: 'Air Conditioning',
    icon: Snowflake,
    color: '#06B6D4',
    description: 'Professional AC installation services',
    path: '/air-conditioning',
    services: [
      'AC Installation',
      'AC Upgrades',
      'System Replacement',
      'Energy-Efficient Units',
      'Proper Sizing'
    ]
  },
  {
    id: 'gas-fitting',
    name: 'Gas Fitting',
    icon: Gauge,
    color: '#F59E0B',
    description: 'Licensed gas fitting & appliance hookups',
    path: '/gas-fitting',
    services: [
      'Gas Line Installation',
      'BBQ Gas Lines',
      'Garage Heater Gas Lines',
      'Equipment & Gas Appliance Installations',
      'Gas Fireplaces'
    ]
  },
  {
    id: 'backflow-testing',
    name: 'Backflow Testing',
    icon: ShieldCheck,
    color: '#10B981',
    description: 'City of Calgary certified backflow testing',
    path: '/plumbing/backflow-testing-calgary',
    services: [
      'Annual Backflow Testing',
      'Cross Connection Control',
      'City of Calgary Compliance',
      'Backflow Preventer Installation'
    ]
  },
  {
    id: 'secondary-suites',
    name: 'Secondary Suites',
    icon: Home,
    color: '#8B5CF6',
    description: 'Legal secondary suite plumbing',
    path: '/plumbing/secondary-suite-plumbing-calgary',
    services: [
      'Basement Suite Plumbing',
      'Drainage Systems',
      'Water Supply Lines',
      'Code-Compliant Installations'
    ]
  }
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;

  return (
    <motion.div
      initial={{ opacity: 1, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group h-full"
    >
      <div 
        data-testid={`service-card-${service.id}`}
        className="relative bg-white rounded-2xl border shadow-lg border-gray-100 hover:shadow-xl hover:border-[#1E3A5F]/30 overflow-hidden h-full transition-all duration-300"
      >
        {/* Card Content */}
        <div className="p-6 pb-4">
          {/* Icon */}
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
            style={{ background: `linear-gradient(135deg, ${service.color}20, ${service.color}10)` }}
          >
            <Icon className="w-7 h-7" style={{ color: service.color }} strokeWidth={1.5} />
          </div>

          {/* Title & Description */}
          <h3 className="font-heading font-bold text-xl text-gray-900 mb-2 group-hover:text-[#1E3A5F] transition-colors">
            {service.name}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {service.description}
          </p>

          {/* All Services - Always Visible */}
          <div className="space-y-2">
            {service.services.slice(0, 6).map((item) => (
              <div key={item} className="flex items-center gap-2 text-gray-600 text-sm">
                <ChevronRight className="w-4 h-4 text-[#1E3A5F] flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2">
          <Link
            to={service.path}
            className="inline-flex items-center gap-1.5 text-[#1E3A5F] text-sm font-semibold hover:gap-2.5 transition-all"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export const ServiceCards = () => {
  return (
    <section data-testid="services-section" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-[#1E3A5F] uppercase tracking-widest text-sm font-semibold">Our Services</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-gray-900 mt-3 mb-5">
            Expert Mechanical Services
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            From repairs to new installations, we deliver quality workmanship for residential, building maintenance, and light commercial clients.
          </p>
        </motion.div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 cta-banner rounded-2xl p-8 md:p-10 text-center"
        >
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
            Fast Service Across Calgary
          </h3>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Reliable service for many plumbing and mechanical issues. Call or text to request your service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#1E3A5F] font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
            >
              Request Service
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+15873553569"
              className="border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              Call Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceCards;
